/**
 * PropertyDetailPage
 * Full property detail page (publicly accessible)
 */

import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Icon from "../../components/AppIcon";
import AIChatWidget from "../../components/public/AIChatWidget";
import AIPropertyReview from "../../components/public/AIPropertyReview";
import PublicFooter from "../../components/public/PublicFooter";
import PublicHeader from "../../components/public/PublicHeader";
import RequestToBuyModal from "../../components/public/RequestToBuyModal";
import RequestToLeaseModal from "../../components/public/RequestToLeaseModal";
import RequestViewingModal from "../../components/public/RequestViewingModal";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery } from "../../store/api/authApi";
import { useGetPublicPropertyByIdQuery } from "../../store/api/publicPropertiesApi";

const PropertyDetailPage: React.FC = () => {
	const params = useParams();
	const id = (params as { id?: string }).id || "";
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	// Only fetch profile if user has a token (skip query if no token to avoid 403 errors)
	const accessToken = tokenStorage.getAccessToken();
	const { data: currentUser } = useGetProfileQuery(
		accessToken ? undefined : skipToken,
	);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);
	const [isCarouselOpen, setIsCarouselOpen] = useState(false);
	const [carouselIndex, setCarouselIndex] = useState(0);
	const [showAIChat, setShowAIChat] = useState(false);
	const [showLeaseModal, setShowLeaseModal] = useState(false);
	const [showBuyModal, setShowBuyModal] = useState(false);
	const [showViewingModal, setShowViewingModal] = useState(false);

	// Check URL params for action
	useEffect(() => {
		const action = searchParams.get("action");
		if (action === "request-lease") {
			setShowLeaseModal(true);
			setSearchParams({}, { replace: true });
		} else if (action === "request-buy") {
			setShowBuyModal(true);
			setSearchParams({}, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	// Fetch property details using RTK Query
	// Skip query if ID is empty or invalid
	const {
		data: property,
		isLoading,
		isError,
		error,
	} = useGetPublicPropertyByIdQuery(id, {
		skip: !id || id.trim() === "",
	});

	// Extract images for useEffect dependency
	const images =
		property?.images && property.images.length > 0 ? property.images : [];

	// Handle keyboard navigation in carousel
	// IMPORTANT: All hooks must be called before any conditional returns
	useEffect(() => {
		if (!isCarouselOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setIsCarouselOpen(false);
			} else if (e.key === "ArrowLeft") {
				setCarouselIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
			} else if (e.key === "ArrowRight") {
				setCarouselIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "";
		};
	}, [isCarouselOpen, images.length]);

	// Format price
	const formatPrice = (price: number | undefined, period?: string) => {
		if (!price) return "Price on request";
		return `₦ ${price.toLocaleString()} ${period ? `/ ${period}` : ""}`;
	};

	// Get rent period label
	const getRentPeriodLabel = (rentType?: string) => {
		switch (rentType) {
			case "annual":
				return "per year";
			case "biannual":
				return "per 6 months";
			case "monthly":
				return "per month";
			case "weekly":
				return "per week";
			default:
				return "per year";
		}
	};

	// Get status badge color
	const getStatusColor = (status: string) => {
		switch (status?.toLowerCase()) {
			case "available":
				return "bg-green-600 text-white border-green-700";
			case "occupied":
				return "bg-gray-700 text-white border-gray-800";
			case "maintenance":
				return "bg-yellow-600 text-white border-yellow-700";
			default:
				return "bg-gray-800 text-white border-gray-900";
		}
	};

	// Loading state
	if (isLoading) {
		return (
			<div className="min-h-screen bg-background">
				<PublicHeader />
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
					<Loader size="lg" showText text="Loading property details..." />
				</div>
				<PublicFooter />
			</div>
		);
	}

	// Error state - also handle case when ID is missing
	if (!id || id.trim() === "") {
		return (
			<div className="min-h-screen bg-background">
				<PublicHeader />
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
					<div className="text-center py-16">
						<div className="inline-flex items-center justify-center w-24 h-24 bg-error/10 rounded-full mb-6">
							<Icon name="AlertCircle" size={48} className="text-error" />
						</div>
						<h2 className="text-2xl font-semibold text-foreground mb-3">
							Invalid Property ID
						</h2>
						<p className="text-muted-foreground mb-6">
							The property ID in the URL is invalid. Please check the link and
							try again.
						</p>
						<Button onClick={() => navigate("/properties")}>
							<Icon name="Home" size={16} className="mr-2" />
							Back
						</Button>
					</div>
				</div>
				<PublicFooter />
			</div>
		);
	}

	// Error state
	if (isError || (!isLoading && !property)) {
		return (
			<div className="min-h-screen bg-background">
				<PublicHeader />
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
					<div className="text-center py-16">
						<div className="inline-flex items-center justify-center w-24 h-24 bg-error/10 rounded-full mb-6">
							<Icon name="AlertCircle" size={48} className="text-error" />
						</div>
						<h2 className="text-2xl font-semibold text-foreground mb-3">
							Property Not Found
						</h2>
						<p className="text-muted-foreground mb-6">
							{error && "data" in error && error.data
								? typeof error.data === "string"
									? error.data
									: typeof error.data === "object" && error.data !== null
									? "detail" in error.data &&
									  typeof error.data.detail === "string"
										? error.data.detail
										: "message" in error.data &&
										  typeof error.data.message === "string"
										? error.data.message
										: "An error occurred while loading the property."
									: "An error occurred while loading the property."
								: "The property you're looking for doesn't exist or has been removed."}
						</p>
						<Button onClick={() => navigate("/properties")}>
							<Icon name="Home" size={16} className="mr-2" />
							Back
						</Button>
					</div>
				</div>
				<PublicFooter />
			</div>
		);
	}

	// Check both rent_amount (new) and monthly_rent (legacy) for lease availability
	const hasRentAmount =
		(property.rent_amount && property.rent_amount > 0) ||
		(property.monthly_rent && property.monthly_rent > 0);
	const isForRent = hasRentAmount;
	const isForSale = property.purchase_price && property.purchase_price > 0;
	const mainImage = images[selectedImageIndex] || null;

	// Open carousel at clicked image
	const openCarousel = (index: number) => {
		setCarouselIndex(index);
		setIsCarouselOpen(true);
	};

	// Navigate carousel
	const goToPrevious = () => {
		setCarouselIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
	};

	const goToNext = () => {
		setCarouselIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
	};

	return (
		<div className="min-h-screen bg-background">
			<PublicHeader />

			<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Back Button */}
				<Button
					variant="ghost"
					onClick={() => navigate("/properties")}
					className="mb-6"
				>
					<Icon name="ArrowLeft" size={16} className="mr-2" />
					Back
				</Button>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					{/* Main Content - Left Side */}
					<div className="lg:col-span-2 space-y-8">
						{/* Image Gallery */}
						<div className="bg-card border border-border rounded-xl overflow-hidden">
							{/* Main Image */}
							<div
								className="relative aspect-video bg-muted cursor-pointer group"
								onClick={() => mainImage && openCarousel(selectedImageIndex)}
							>
								{mainImage ? (
									<>
										<img
											src={mainImage}
											alt={property.name}
											className="w-full h-full object-cover pointer-events-none"
										/>
										{/* Click indicator overlay */}
										<div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 smooth-transition flex items-center justify-center pointer-events-none">
											<div className="opacity-0 group-hover:opacity-100 smooth-transition bg-black/50 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
												<Icon name="Maximize2" size={20} />
												<span className="text-sm font-medium">
													Click to view
												</span>
											</div>
										</div>
									</>
								) : (
									<div className="w-full h-full flex items-center justify-center">
										<Icon
											name="Building"
											size={96}
											className="text-muted-foreground opacity-30"
										/>
									</div>
								)}

								{/* Status Badge */}
								<div className="absolute top-4 left-4 z-10">
									<span
										className={`px-4 py-2 rounded-full text-sm font-semibold border shadow-lg backdrop-blur-md ${getStatusColor(
											property.status,
										)}`}
									>
										{property.status?.charAt(0).toUpperCase() +
											property.status?.slice(1)}
									</span>
								</div>
							</div>

							{/* Image Thumbnails */}
							{images.length > 1 && (
								<div className="p-4 grid grid-cols-4 sm:grid-cols-6 gap-2">
									{images.map((image, index) => (
										<button
											key={index}
											onClick={() => {
												setSelectedImageIndex(index);
												openCarousel(index);
											}}
											className={`aspect-square rounded-lg overflow-hidden border-2 smooth-transition cursor-pointer ${
												index === selectedImageIndex
													? "border-primary"
													: "border-transparent hover:border-border"
											}`}
										>
											<img
												src={image}
												alt={`${property.name} - ${index + 1}`}
												className="w-full h-full object-cover"
											/>
										</button>
									))}
								</div>
							)}
						</div>

						{/* Property Description */}
						<div className="bg-card border border-border rounded-xl p-6">
							<h2 className="text-2xl font-semibold text-foreground mb-4">
								Description
							</h2>
							<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
								{property.description || "No description available."}
							</p>
						</div>

						{/* Features */}
						{property.features && property.features.length > 0 && (
							<div className="bg-card border border-border rounded-xl p-6">
								<h2 className="text-2xl font-semibold text-foreground mb-4">
									Property Features
								</h2>
								<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
									{property.features.map((feature, index) => (
										<div
											key={index}
											className="flex items-center space-x-2 text-muted-foreground"
										>
											<Icon name="Check" size={16} className="text-success" />
											<span>{feature}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{/* AI Property Review */}
						<AIPropertyReview propertyId={id} propertyName={property.name} />
					</div>

					{/* Sidebar - Right Side */}
					<div className="lg:col-span-1">
						<div className="bg-card border border-border rounded-xl p-6 sticky top-20 space-y-6">
							{/* Property Title */}
							<div>
								<h1 className="text-3xl font-bold text-foreground mb-2">
									{property.name}
								</h1>
								<div className="flex flex-col gap-1">
									{/* Unit Number - if exists */}
									{property.unit_number && (
										<div className="flex items-center text-muted-foreground">
											<Icon name="Hash" size={16} className="mr-1" />
											<span className="font-medium">{property.unit_number}</span>
										</div>
									)}

									{/* Address */}
									<div className="flex items-center text-muted-foreground">
										<Icon name="MapPin" size={16} className="mr-1" />
										<span>
											{property.address}, {property.city}, {property.state}
										</span>
									</div>
								</div>
							</div>

							{/* Price */}
							<div className="pt-6 border-t border-border">
								{isForRent && isForSale ? (
									// Dual pricing layout
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/40 rounded-lg border border-border">
										<div className="flex flex-col">
											<div className="flex items-center gap-2 mb-2">
												<Icon name="Key" size={16} className="text-blue-600" />
												<span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
													For Lease
												</span>
											</div>
											<p className="text-lg font-bold text-primary break-words">
												{property.rent_amount
													? formatPrice(
															property.rent_amount,
															getRentPeriodLabel(property.rent_type),
													  )
													: formatPrice(property.monthly_rent, "month")}
											</p>
										</div>
										<div className="flex flex-col sm:border-l sm:border-border sm:pl-4">
											<div className="flex items-center gap-2 mb-2">
												<Icon name="Tag" size={16} className="text-green-600" />
												<span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
													For Sale
												</span>
											</div>
											<p className="text-lg font-bold text-primary break-words">
												{formatPrice(property.purchase_price)}
											</p>
										</div>
									</div>
								) : isForRent ? (
									// Only lease available
									<div>
										<p className="text-2xl font-bold text-primary break-words">
											{property.rent_amount
												? formatPrice(
														property.rent_amount,
														getRentPeriodLabel(property.rent_type),
												  )
												: formatPrice(property.monthly_rent, "month")}
										</p>
										<p className="text-sm text-muted-foreground mt-1">
											For Lease
										</p>
									</div>
								) : isForSale ? (
									// Only sale available
									<div>
										<p className="text-2xl font-bold text-primary break-words">
											{formatPrice(property.purchase_price)}
										</p>
										<p className="text-sm text-muted-foreground mt-1">
											For Sale
										</p>
									</div>
								) : (
									// No pricing available
									<p className="text-2xl font-bold text-muted-foreground">
										Price on request
									</p>
								)}
							</div>

							{/* Key Details */}
							<div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
								{property.bedrooms > 0 && (
									<div className="text-center">
										<Icon
											name="Bed"
											size={24}
											className="mx-auto mb-2 text-primary"
										/>
										<p className="text-sm font-medium text-foreground">
											{property.bedrooms}
										</p>
										<p className="text-xs text-muted-foreground">Bedrooms</p>
									</div>
								)}

								{property.bathrooms > 0 && (
									<div className="text-center">
										<Icon
											name="Bath"
											size={24}
											className="mx-auto mb-2 text-primary"
										/>
										<p className="text-sm font-medium text-foreground">
											{property.bathrooms}
										</p>
										<p className="text-xs text-muted-foreground">Bathrooms</p>
									</div>
								)}

								{property.square_feet > 0 && (
									<div className="text-center">
										<Icon
											name="Maximize"
											size={24}
											className="mx-auto mb-2 text-primary"
										/>
										<p className="text-sm font-medium text-foreground">
											{property.square_feet}
										</p>
										<p className="text-xs text-muted-foreground">sqft</p>
									</div>
								)}
							</div>

							{/* Property Type */}
							{property.property_type && (
								<div className="pt-6 border-t border-border">
									<p className="text-sm text-muted-foreground mb-1">
										Property Type
									</p>
									<p className="text-base font-medium text-foreground capitalize">
										{property.property_type}
									</p>
								</div>
							)}

							{/* Action Buttons */}
							<div className="space-y-3 pt-6 border-t border-border">
								<Button
									size="lg"
									className="w-full"
									onClick={() => setShowViewingModal(true)}
									iconName="Calendar"
									iconPosition="left"
								>
									Request Viewing
								</Button>

								{isForRent && (
									<Button
										size="lg"
										variant="outline"
										className="w-full"
										onClick={() => setShowLeaseModal(true)}
									>
										Rent
									</Button>
								)}
								{isForSale && (
									<Button
										size="lg"
										variant="outline"
										className="w-full"
										onClick={() => setShowBuyModal(true)}
									>
										Buy
									</Button>
								)}

								<Button
									size="lg"
									variant="outline"
									className="w-full"
									onClick={() => setShowAIChat(!showAIChat)}
									iconName="MessageSquare"
									iconPosition="left"
								>
									AI Chat
								</Button>

								{!currentUser && (
									<Button
										size="lg"
										variant="outline"
										className="w-full"
										onClick={() => navigate("/register")}
									>
										<Icon name="UserPlus" size={20} className="mr-2" />
										Sign Up
									</Button>
								)}
							</div>

							{/* Contact Info */}
							<div className="pt-6 border-t border-border">
								<p className="text-xs text-muted-foreground text-center">
									Sign up to contact the property manager and schedule a viewing
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Image Carousel Modal */}
			{isCarouselOpen && images.length > 0 && (
				<div
					className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
					onClick={() => setIsCarouselOpen(false)}
				>
					{/* Close Button */}
					<button
						onClick={() => setIsCarouselOpen(false)}
						className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white smooth-transition"
						aria-label="Close carousel"
					>
						<Icon name="X" size={24} />
					</button>

					{/* Image Counter */}
					<div className="absolute top-4 left-4 z-10 px-4 py-2 rounded-full bg-black/50 text-white text-sm font-medium">
						{carouselIndex + 1} / {images.length}
					</div>

					{/* Previous Button */}
					{images.length > 1 && (
						<button
							onClick={(e) => {
								e.stopPropagation();
								goToPrevious();
							}}
							className="absolute left-4 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white smooth-transition"
							aria-label="Previous image"
						>
							<Icon name="ChevronLeft" size={24} />
						</button>
					)}

					{/* Main Image */}
					<div
						className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4"
						onClick={(e) => e.stopPropagation()}
					>
						<img
							src={images[carouselIndex]}
							alt={`${property.name} - ${carouselIndex + 1}`}
							className="max-w-full max-h-full object-contain rounded-lg"
						/>
					</div>

					{/* Next Button */}
					{images.length > 1 && (
						<button
							onClick={(e) => {
								e.stopPropagation();
								goToNext();
							}}
							className="absolute right-4 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white smooth-transition"
							aria-label="Next image"
						>
							<Icon name="ChevronRight" size={24} />
						</button>
					)}

					{/* Thumbnail Strip (Optional - for quick navigation) */}
					{images.length > 1 && (
						<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 flex space-x-2 max-w-full overflow-x-auto px-4">
							{images.map((image, index) => (
								<button
									key={index}
									onClick={(e) => {
										e.stopPropagation();
										setCarouselIndex(index);
									}}
									className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 smooth-transition ${
										index === carouselIndex
											? "border-white"
											: "border-transparent hover:border-white/50"
									}`}
								>
									<img
										src={image}
										alt={`Thumbnail ${index + 1}`}
										className="w-full h-full object-cover"
									/>
								</button>
							))}
						</div>
					)}
				</div>
			)}

			<PublicFooter />

			{/* AI Chat Widget */}
			{showAIChat && (
				<AIChatWidget
					propertyId={id}
					propertyName={property.name}
					onClose={() => setShowAIChat(false)}
				/>
			)}

			{/* Request to Lease Modal */}
			{property && (
				<RequestToLeaseModal
					property={property}
					isOpen={showLeaseModal}
					onClose={() => setShowLeaseModal(false)}
				/>
			)}

			{/* Request to Buy Modal */}
			{property && (
				<RequestToBuyModal
					property={property}
					isOpen={showBuyModal}
					onClose={() => setShowBuyModal(false)}
				/>
			)}

			{/* Request Viewing Modal */}
			{property && (
				<RequestViewingModal
					propertyId={property.id}
					propertyName={property.name}
					isOpen={showViewingModal}
					onClose={() => setShowViewingModal(false)}
				/>
			)}
		</div>
	);
};

export default PropertyDetailPage;
